import { Controller } from '@hotwired/stimulus';
import { getCsrfToken } from '../lib/csrf';

// Custom dropdown for profile selection in the invest modal.
// Animates open/close with a height transition.
//
// Close on click outside via data-action on the body
// or a wrapper element:
//   data-action="click@document->profile-selector#closeOnClickOutside"
export default class extends Controller {
  static targets = ['input', 'trigger', 'menu', 'option', 'selectedName', 'selectedBalance'];
  static values = { switchUrl: String };

  connect() {
    this.isOpen = false;

    const menu = this.menuTarget;
    menu.style.overflow = 'hidden';
    menu.style.transition = 'height 0.2s ease, opacity 0.2s ease';
    menu.style.height = '0';
    menu.style.opacity = '0';
    menu.classList.remove('d-none');
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    const menu = this.menuTarget;
    menu.style.height = `${menu.scrollHeight}px`;
    menu.style.opacity = '1';
    this.isOpen = true;
  }

  close() {
    const menu = this.menuTarget;
    menu.style.height = '0';
    menu.style.opacity = '0';
    this.isOpen = false;
  }

  closeOnClickOutside(event) {
    if (this.isOpen && !this.element.contains(event.target)) {
      this.close();
    }
  }

  select(event) {
    const option = event.currentTarget;
    if (option.dataset.disabled === 'true') return;

    const newValue = option.dataset.value;
    if (this.inputTarget.value === newValue) {
      this.close();
      return;
    }

    this.inputTarget.value = newValue;

    if (this.hasSelectedNameTarget) {
      this.selectedNameTarget.textContent = option.dataset.name;
    }
    if (this.hasSelectedBalanceTarget) {
      this.selectedBalanceTarget.textContent = option.dataset.balance;
    }

    this.optionTargets.forEach(opt => {
      opt.classList.toggle('bg-primary-50', opt === option);
    });

    this.dispatch('change', {
      detail: {balanceRaw: parseFloat(option.dataset.balanceRaw) || 0},
    });

    this.#syncServerProfile(newValue);

    this.close();
  }

  // Fire-and-forget call to move the selected profile to the top of the
  // user's profile list, so that `current_profile` on the server side
  // reflects the selection for any subsequent navigation triggered from
  // the slideover (credit account, questionnaire, etc.).
  #syncServerProfile(profileId) {
    if (!this.hasSwitchUrlValue || !this.switchUrlValue) return;

    fetch(this.switchUrlValue, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
      },
      body: JSON.stringify({profile_id: profileId, silent: true}),
      credentials: 'same-origin',
    }).catch((err) => {
      console.warn('Profile silent switch failed', err);
    });
  }
}
